import { Modal, Form, Button, Card, Table } from 'react-bootstrap'
import { useContext, useEffect, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import { Calendar } from 'react-feather'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import callAPI from '../../../../../helpers/callAPI'
import TriggerModal from '../MobileKPI/TriggerModal'
import useCallApi from '../../../../../hooks/useCallApi'
import calculateAffiliateRate from '../../../../../helpers/calculateAffRate'
import { SystemMessageContext } from '../../../../../context/SystemMessageContext'
import ScheduledRateChangeBanner from './ScheduledRateChangeBanner/ScheduledRateChangeBanner'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const RatesModal = ({ campaign, campaignId, isOpen, setIsOpen, selectedRateName, isCreate, refetch, ratePitchId, ratePitchScreen }) => {
    const { showSystemMessage } = useContext(SystemMessageContext)
    const [isScheduleMode, setIsScheduleMode] = useState(false)
    const [scheduleDate, setScheduleDate] = useState(null)
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false)
    const [formData, setFormData] = useState(false)
    const [conversionTypes, setConversionTypes] = useState([])
    const [isMobileOffer, setIsMobileOffer] = useState(false)
    const [events, setEvents] = useState([])
    const [properties, setProperties] = useState([])
    const [propertyId, setPropertyId] = useState(0)
    const [selectedRate, setSelectedRate] = useState({
        affiliate_rate: '',
        conversion_type: '',
        default_flag: '',
        description: '',
        has_trigger: '',
        is_recurring: '',
        network_rate: '',
        rate_name: '',
        rate_warning: 0,
        special_rate_count: 0,
        rate_pitch_id: ratePitchId && ratePitchId > 0 ? parseInt(ratePitchId) : null,
    })

    const { data: campaignData, dataLoaded: campaignDataLoaded } = useCallApi(`campaign/${campaignId}`, 'GET', {}, [campaignId])

    const pendingRegularRateSchedule = useMemo(() => {
        if (isCreate || !campaignDataLoaded || !campaignData?.campaign?.scheduled_jobs?.length) return null
        return (
            campaignData.campaign.scheduled_jobs.find(
                (j) =>
                    (j.name === 'RATE_SCH') && j.rate_name === selectedRateName
            ) || null
        )
    }, [isCreate, campaignDataLoaded, campaignData, selectedRateName])
    
    useEffect(() => {
        //find rate
        if (campaignDataLoaded && campaignData.campaign && !isCreate) {
            const rate = campaignData.campaign.rates.find((r) => r.rate_name === selectedRateName)
            setSelectedRate({ ...rate, rate_pitch_id: ratePitchId && ratePitchId > 0 ? parseInt(ratePitchId) : null })
            setFormData({ ...rate, rate_pitch_id: ratePitchId && ratePitchId > 0 ? parseInt(ratePitchId) : null })
            setIsMobileOffer(campaignData.campaign.tracking.is_mobile_offer)
            setEvents(campaignData.campaign.mobile_in_app_events || [])
        }
    }, [campaignDataLoaded, selectedRateName, isCreate, ratePitchId])

    const { data: triggerData, dataLoaded: triggerDataLoaded, refetch: triggerRefetch } = useCallApi(`campaign/${campaignId}/triggers?rate_name=${!isCreate&&selectedRate.rate_name}&mode=rate`, 'GET', {}, [campaignId, campaignData, selectedRate])

    const [triggerModalData, setTriggerModalData] = useState({ isOpen: false, data: { event_mode: '', kpi_trigger_id: '', monetary_value: '', related_event_name: '', related_event_period: '', required_event_name: '' }, isCreate: false, trigger_id: 0 })

    useEffect(async () => {
        setFormData(selectedRate)

        if (conversionTypes.length === 0) {
            const result = await callAPI(`conversionTypes`, 'GET')
            if (result.success) {
                setConversionTypes(result.conversion_types)
            }
        }

        if (campaignData.campaign && campaignData.campaign.type.value === 'A') {
            const result = await callAPI(`avatar/properties`, 'GET')
            if (result.success) {
                setProperties(result.properties)
            }

            const avatarResult = await callAPI(`campaign/${campaignId}/rates/avatar?rateName=${selectedRate.rate_name}`, 'GET')

            if (avatarResult.success) {
                setPropertyId(avatarResult.property_id)
            }
        }
    }, [selectedRate])

    const closeModal = () => {
        setIsScheduleMode(false)
        setScheduleDate(null)
        setIsOpen(false)
    }

    useEffect(() => {
        if (!isOpen) {
            setIsScheduleMode(false)
            setScheduleDate(null)
            setScheduleSubmitting(false)
        }
    }, [isOpen])

    const handleScheduleRateChange = async () => {
        if (!scheduleDate) {
            showSystemMessage('error', 'Please select a date and time')
            return
        }

        const dr = parseFloat(formData.display_rate)
        const dnr = parseFloat(formData.display_net_rate)
        if (dr > 0 && (!formData.display_rate_type || formData.display_rate_type === '')) {
            showSystemMessage('error', 'If you are setting a display rate override, you must select a display rate type.')
            return
        }
        if (dr > 0 && (formData.display_net_rate === '' || formData.display_net_rate == null || isNaN(dnr))) {
            showSystemMessage('error', 'If you are setting a display rate, you need to set the display net rate.')
            return
        }

        setScheduleSubmitting(true)
        const body = {
            date: moment(scheduleDate).format('YYYY-MM-DD HH:mm:ss'),
            rate_name: formData.rate_name,
            affiliate_rate: String(formData.affiliate_rate),
            network_rate: String(formData.network_rate),
            display_rate: formData.display_rate !== '' && formData.display_rate != null ? String(formData.display_rate) : '',
            display_net_rate: formData.display_net_rate !== '' && formData.display_net_rate != null ? String(formData.display_net_rate) : '',
            display_rate_type: formData.display_rate_type || '',
            offer_type: formData.rate_type || '',
            description: formData.description || '',
        }

        try {
            const result = await callAPI(`campaign/${campaignId}/scheduleRateChange`, 'POST', body)
            if (result.success === true) {
                showSystemMessage('success', result.message || 'Rate change scheduled.')
                closeModal()
                refetch()
            } else if (result.success === false) {
                showSystemMessage('error', result.errors?.message || 'Could not schedule rate change.')
            }
        } catch (error) {
            showSystemMessage('error', error.message)
        } finally {
            setScheduleSubmitting(false)
        }
    }
    
    const buildRateBody = () => {
        if (!formData) {
            return null
        }
        const { open_rate_pitch_id: _omitId, open_rate_pitch_ids: _omitIds, ratePitchScreen, ...rest } = formData
        const body = { ...rest, offer_type: campaignData.campaign.type.value }
        if (campaignData.campaign.type.value === 'A') {
            body.property_id = propertyId
        }
        return body
    }

    const createTracking = async (e) => {
        e.preventDefault()

        const body = buildRateBody()
        if (!body || !campaignData?.campaign) {
            return
        }

        const result = await callAPI(`campaign/${campaignId}/rate`, 'POST', body)
        if (result.success) {
            showSystemMessage('success', 'Rate created successfully')
            setIsOpen(false)
            refetch()
        } else {
            showSystemMessage('error', result.errors.message)
        }
    }

    const updateTracking = async (e) => {
        e.preventDefault()

        const body = buildRateBody()
        if (!body || !campaignData?.campaign) {
            return
        }

        const result = await callAPI(`campaign/${campaignId}/rate`, 'PUT', body)
        if (result.success) {
            showSystemMessage('success', 'Rate updated successfully')
            setIsOpen(false)
            refetch()
        } else {
            showSystemMessage('error', result.errors.message)
        }
    }

    const mergePitchIdsForLinks = () => {
        const seen = new Set()
        const ordered = []
        const push = (raw) => {
            const id = parseInt(raw, 10)
            if (id > 0 && !seen.has(id)) {
                seen.add(id)
                ordered.push(id)
            }
        }
        if (ratePitchId) {
            push(ratePitchId)
        }
        if (formData && Array.isArray(formData.open_rate_pitch_ids)) {
            formData.open_rate_pitch_ids.forEach(push)
        }
        if (formData && formData.open_rate_pitch_id > 0) {
            push(formData.open_rate_pitch_id)
        }
        return ordered
    }
    const pitchIdsForLink = mergePitchIdsForLinks()

    return (
        <Modal show={isOpen} onHide={closeModal} size={'md'}>
            <Form onSubmit={isCreate ? createTracking : updateTracking}>
                <Modal.Header closeButton>
                    <h1 className="mb-h1-dark">
                        {isCreate ? 'Create' : 'Edit'} Rate for Offer: {campaignDataLoaded && campaignData.campaign.name}
                    </h1>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                        <span>Rate Name: </span>
                        <div className="w-75">
                            <Form.Control disabled={isCreate ? false : true} type="text" value={formData.rate_name} onChange={(e) => setFormData({ ...formData, rate_name: e.target.value })} required />
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                        <span>Description: </span>
                        <div className="w-75">
                            <Form.Control type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                        <span style={{ width: '20%' }}>Default Rate: </span>
                        <div className="w-75">
                            <Form.Check type="checkbox" checked={formData.default_flag} onChange={(e) => setFormData({ ...formData, default_flag: e.target.checked })} disabled={selectedRate.default_flag} />
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                        <span style={{ width: '20%' }}>Rate Type: </span>
                        <div className="w-75">
                            <Form.Control as="select" value={formData.rate_type} disabled={formData.rate_type === 'N'} onChange={(e) => setFormData({ ...formData, rate_type: e.target.value })} required>
                                <option value=""></option>
                                <option value="F">$ per lead</option>
                                <option value="P">% of sale</option>
                                {formData.rate_type === 'N' && <option value="N">% of commission</option>}
                                {campaignDataLoaded && campaignData.campaign.type.value === 'A' && (
                                    <>
                                        <option value="M">$ aff / % net</option>
                                        <option value="C">$ per click</option>
                                    </>
                                )}
                            </Form.Control>
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                        <span>Affiliate Rate: </span>
                        <div className="w-75 d-flex">
                            <div className="w-50 d-flex align-items-center">
                                <span className="mx-2">Actual: </span>
                                <Form.Control
                                    disabled={formData.display_rate > 0 && formData.display_net_rate > 0}
                                    type="text"
                                    value={formData.affiliate_rate}
                                    onChange={(e) => {
                                        setFormData({ ...formData, affiliate_rate: e.target.value })
                                    }}
                                    required
                                />
                            </div>
                            <div className="w-50 d-flex align-items-center">
                                <span className="mx-2 text-info font-weight-bold">Display: </span>
                                <Form.Control
                                    type="text"
                                    value={formData.display_rate}
                                    onChange={(e) => {
                                        setFormData({ ...formData, affiliate_rate: calculateAffiliateRate(formData.affiliate_rate, formData.network_rate, e.target.value, formData.display_net_rate), display_rate: e.target.value })
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {formData.display_rate > 0 && formData.display_net_rate > 0 && (
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span></span>
                            <div className="w-75 d-flex text-secondary">* Will be calculated to match display rate.</div>
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                        <span>Network Rate: </span>
                        <div className="w-75  d-flex">
                            <div className="w-50 d-flex align-items-center">
                                <span className="mx-2">Actual: </span>
                                <Form.Control
                                    disabled={(formData.display_rate > 0 && formData.display_net_rate > 0) || formData.rate_type === 'N'}
                                    type="text"
                                    value={formData.network_rate}
                                    onChange={(e) => {
                                        setFormData({ ...formData, network_rate: e.target.value })
                                    }}
                                    required
                                />
                            </div>
                            <div className="w-50 d-flex align-items-center">
                                <span className="mx-2 text-info font-weight-bold">Display: </span>
                                <Form.Control
                                    type="text"
                                    value={formData.display_net_rate}
                                    onChange={(e) => {
                                        setFormData({ ...formData, affiliate_rate: calculateAffiliateRate(formData.affiliate_rate, formData.network_rate, formData.display_rate, e.target.value), display_net_rate: e.target.value })
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {!isCreate && pitchIdsForLink.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span />
                            <div className="w-75 d-flex flex-wrap align-items-center">
                                {pitchIdsForLink.map((pid, idx) => (
                                    <span key={pid} className={idx > 0 ? 'ml-2' : ''}>
                                        <Link target="_blank" to={`/advertisers/ratePitch/${pid}`}><FontAwesomeIcon className="ml-2 text-primary" icon={faExternalLinkAlt} /> View Active Rate Pitch ({pid}) </Link>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {isMobileOffer && (
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span style={{ width: '20%' }}>Recurring: </span>
                            <div className="w-75">
                                <Form.Check type="checkbox" checked={formData.is_recurring} onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })} />
                            </div>
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                        <span>Status: </span>
                        <div className="w-75">
                            <Form.Control as="select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} required>
                                <option value=""></option>
                                <option value="A">Active (Visible)</option>
                                <option value="H">Inactive (Hidden)</option>
                            </Form.Control>
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                        <span>Conversion Type: </span>
                        <div className="w-75">
                            <Form.Control as="select" value={formData.conversion_type_id} onChange={(e) => setFormData({ ...formData, conversion_type_id: e.target.value })} required>
                                <option value="">Select conversion type</option>
                                {conversionTypes.map((item, index) => (
                                    <option key={index} value={item.conversion_type_id}>
                                        {item.conversion_type}
                                    </option>
                                ))}
                            </Form.Control>
                        </div>
                    </div>
                    {campaignDataLoaded && campaignData.campaign.type.value === 'A' && formData.rate_type === 'C' && (
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                            <span className="w-25">Avatar property for tracking CPC rate: </span>
                            <div className="w-75">
                                <Form.Control as="select" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} required>
                                    <option value=""></option>
                                    {properties.map((item) => (
                                        <option key={item.property_id} value={item.property_id}>
                                            {item.domain}
                                        </option>
                                    ))}
                                </Form.Control>
                            </div>
                        </div>
                    )}
                    <div className="card">
                        <div className="card-header text-info">Display Rate Settings</div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                                <span style={{ width: '20%' }}>Hide Sale Amount: </span>
                                <div className="w-75">
                                    <Form.Check type="checkbox" checked={formData.hide_sale_amount} onChange={(e) => setFormData({ ...formData, hide_sale_amount: e.target.checked })} />
                                </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                                <span style={{ width: '20%' }}>Rate Type: </span>
                                <div className="w-75">
                                    <div>
                                        <Form.Control as="select" value={formData.display_rate_type} onChange={(e) => setFormData({ ...formData, display_rate_type: e.target.value })}>
                                            <option value=""></option>
                                            <option value="F">$ per lead</option>
                                            <option value="P">% of sale</option>
                                            {campaignDataLoaded && campaignData.campaign.type.value === 'A' && (
                                                <>
                                                    <option value="M">$ aff / % net</option>
                                                    <option value="C">$ per click</option>
                                                </>
                                            )}
                                        </Form.Control>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {triggerModalData.isOpen && <TriggerModal mode="rate" events={events} refetch={triggerRefetch} campaignId={campaignId} showSystemMessage={showSystemMessage} isOpen={triggerModalData.isOpen} closeModal={() => setTriggerModalData({ ...triggerModalData, isOpen: false })} data={triggerModalData.data} isCreate={triggerModalData.isCreate} rateName={formData.rate_name} trigger_id={triggerModalData.trigger_id} />}
                    {!isCreate && isMobileOffer && (
                        <Card className="mt-2">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                Triggers
                                {events.length === 0 ? (
                                    <button className="btn btn-sm btn-warning" disabled={true}>
                                        No events
                                    </button>
                                ) : (
                                    <Button
                                        variant="warning"
                                        size="sm"
                                        onClick={() => {
                                            setTriggerModalData({ isOpen: true, isCreate: true })
                                        }}
                                    >
                                        Add Trigger
                                    </Button>
                                )}
                            </Card.Header>
                            <Table striped bordered hover className="m-0">
                                <thead>
                                    <tr>
                                        <th>Event</th>
                                        <th>Hours after</th>
                                        <th>Event</th>
                                        <th>Monetary Value</th>
                                        <th>Mode</th>
                                        <th>Edit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {triggerData?.triggers?.length > 0 &&
                                        triggerData.triggers.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.required_event_name}</td>
                                                <td>{item.trigger_period}</td>
                                                <td>{item.related_event_name}</td>
                                                <td>{item.monetary_value}</td>
                                                <td>{item.event_mode === 'SUM' ? 'Sum' : item.event_mode === 'IND' ? 'Individual' : ''}</td>
                                                <td>
                                                    <Button
                                                        variant="warning"
                                                        size="sm"
                                                        onClick={() => {
                                                            setTriggerModalData({ ...triggerModalData, isCreate: false, trigger_id: item.trigger_id, isOpen: true })
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </Table>
                        </Card>
                    )}
                </Modal.Body>
                <Modal.Footer className={!isCreate && isScheduleMode ? 'flex-column align-items-stretch' : ''}>
                    {!isCreate && pendingRegularRateSchedule && (
                        <ScheduledRateChangeBanner
                            jobDate={pendingRegularRateSchedule.date}
                            affiliateRate={pendingRegularRateSchedule.param1}
                            networkRate={pendingRegularRateSchedule.param2}
                            displayRate={pendingRegularRateSchedule.param3}
                            displayNetRate={pendingRegularRateSchedule.param4}
                            currency={campaign.currency}
                            rateType={
                                parseFloat(pendingRegularRateSchedule.param3) > 0 && parseFloat(pendingRegularRateSchedule.param4) > 0
                                    ? pendingRegularRateSchedule.param7
                                    : pendingRegularRateSchedule.param6
                            }
                        />
                    )}
                    {isCreate ? (
                        <Button variant="primary" type="submit">
                            Create
                        </Button>
                    ) : isScheduleMode ? (
                        <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: '0.5rem' }}>
                            <span className="mb-0 mr-2">Change rate on:</span>
                            <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: '200px' }}>
                                <DatePicker
                                    id="rate_schedule_date_time"
                                    placeholderText="Select Date & Time"
                                    className="form-control"
                                    selected={scheduleDate}
                                    minDate={moment().toDate()}
                                    showTimeSelect
                                    dateFormat="yyyy/MM/dd HH:mm:ss"
                                    onChange={(d) => {
                                        const el = document.getElementById('rate_schedule_date_time')
                                        if (el) el.setCustomValidity('')
                                        setScheduleDate(d)
                                    }}
                                    autoComplete="off"
                                />
                                <Calendar className="ml-1 flex-shrink-0" size={16} color="#007bff" />
                            </div>
                            <div className="d-flex ml-auto" style={{ gap: '0.5rem' }}>
                                <Button
                                    variant="outline-danger"
                                    type="button"
                                    disabled={scheduleSubmitting}
                                    onClick={() => {
                                        setIsScheduleMode(false)
                                        setScheduleDate(null)
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button variant="primary" type="button" disabled={scheduleSubmitting} onClick={handleScheduleRateChange}>
                                    {scheduleSubmitting ? 'Scheduling…' : 'Schedule'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-end w-100" style={{ gap: '0.5rem' }}>
                            <Button variant="warning" type="button" onClick={() => setIsScheduleMode(true)}>
                                Schedule Rate Change
                            </Button>
                            <Button variant="primary" type="submit">
                                Update
                            </Button>
                        </div>
                    )}
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

export default RatesModal
